import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { PrismStatusIndicatorComponent, PrismTooltipComponent } from '@shure/prism-angular-components';
import { DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

/**
 * ```sh-device-state-bar``` is a view component for displaying color codes based on the DeviceState of a device.
 */
@Component({
	selector: 'sh-device-state-bar',
	templateUrl: './device-state-bar.component.html',
	styleUrls: ['./device-state-bar.component.scss'],
	imports: [CommonModule, PrismTooltipComponent, PrismStatusIndicatorComponent, TranslocoModule],
	providers: [provideInlineScope('scDeviceStateBar', (lang) => import(`../i18n/${lang}`))]
})
export class DeviceStateBarComponent {
	public readonly deviceStatus = input.required<DeviceStatus>();

	protected readonly statusTooltip = computed(() => this.getTooltipMessage(this.deviceStatus()));
	protected readonly prismState = computed(() => this.mapToPrismState(this.deviceStatus()));

	constructor(private readonly translocoService: TranslocoService) {}

	private getTooltipMessage(status: DeviceStatus): string {
		switch (status) {
			case DeviceStatus.Online:
				return this.translocoService.translate('scDeviceStateBar.online');
			case DeviceStatus.Offline:
				return this.translocoService.translate('scDeviceStateBar.offline');
			case DeviceStatus.Error:
				return this.translocoService.translate('scDeviceStateBar.device-discovery-error');
			default:
				return '';
		}
	}

	private mapToPrismState(status: DeviceStatus): string {
		switch (status) {
			case DeviceStatus.Online:
				return 'good';
			case DeviceStatus.Offline:
				return 'inactive';
			case DeviceStatus.Discovered:
				return 'ok';
			case DeviceStatus.Error:
				return 'bad';
			default:
				return '';
		}
	}
}
